import React, { memo, useState, useCallback, useEffect, useRef } from 'react';
import { SubitemsService } from '../services/SubitemsService';
import SubitemStatusIndicator from './SubitemStatusIndicator';
import { supabase } from '../lib/supabase';
import { useRealtimeSubitems } from '../hooks/useRealtimeSubitems';
import { PHASE_ORDER, getPhaseForStatus } from '../constants/phaseStatusMapping';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';

// Using centralized phase order from phaseStatusMapping.js

// Stage colors configuration matching the tab colors
const STAGE_COLORS = {
  '00-Startup': { bg: '#6b7280', text: '#ffffff' },        // Gray
  '01-Data Prep': { bg: '#f59e0b', text: '#ffffff' },      // Amber
  '02-Utilities': { bg: '#10b981', text: '#ffffff' },       // Emerald
  '03-Discrepancy Check': { bg: '#ef4444', text: '#ffffff' }, // Red
  '04-Erosion/Pond': { bg: '#06b6d4', text: '#ffffff' },   // Cyan
  '05-Modeling': { bg: '#8b5cf6', text: '#ffffff' },       // Violet
  '06-Double Check': { bg: '#f97316', text: '#ffffff' },   // Orange
  '07-Corrections': { bg: '#ec4899', text: '#ffffff' },    // Pink
  '08-Review': { bg: '#6366f1', text: '#ffffff' },         // Indigo
  '09-Ready to Export': { bg: '#10b981', text: '#ffffff' }, // Emerald
  '10-Completed': { bg: '#6b7280', text: '#ffffff' }       // Gray
};

// Using centralized phase mapping via getPhaseForStatus helper

/**
 * SubitemsTable
 *
 * Specialized table for displaying project subitems with three-section structure:
 * - Current stage items
 * - Previous stages not completed
 * - All project subitems (collapsible)
 *
 * Features:
 * - Real-time updates via Supabase subscriptions
 * - Phase-based grouping with color-coded badges
 * - Status indicators with dropdown selection
 * - Delayed removal animation (3 seconds)
 * - Special highlighting for completed/stuck items
 * - Automatic user assignment on status change
 * - Dark theme optimized for AB Civil design system
 */
const SubitemsTable = memo(({ subitems, loading, error, onSubitemUpdate, employees, modelingStatus, projectId }) => {
  const [updatingItems, setUpdatingItems] = useState(new Set());
  const [itemsToRemove, setItemsToRemove] = useState(new Set());
  const [completedItems, setCompletedItems] = useState(new Map()); // Track completed items with timestamps
  const [allItemsCollapsed, setAllItemsCollapsed] = useState(true); // All Project Subitems collapsed by default
  const [localSubitems, setLocalSubitems] = useState(subitems || []);
  const timersRef = useRef(new Map()); // Track removal timers
  const allItemsSectionRef = useRef(null); // Ref for all items section
  
  // Update local subitems when props change
  useEffect(() => {
    setLocalSubitems(subitems || []);
  }, [subitems]);
  
  // Handle real-time updates
  const handleRealtimeSubitemUpdate = useCallback((updatedSubitem) => {
    console.log('🔴 Handling real-time subitem update in table:', updatedSubitem);
    
    // Update local state immediately
    setLocalSubitems(prevSubitems => {
      const newSubitems = prevSubitems.map(item => 
        item.id === updatedSubitem.id ? { ...item, ...updatedSubitem } : item
      );
      console.log('🔴 Updated local subitems:', newSubitems);
      return newSubitems;
    });
    
    // Also call the parent update handler
    if (onSubitemUpdate) {
      onSubitemUpdate(updatedSubitem);
    }
  }, [onSubitemUpdate]);
  
  // Set up real-time subscription
  useRealtimeSubitems(projectId, handleRealtimeSubitemUpdate, true);
  
  // Helper function to get phase index for ordering
  const getPhaseIndex = (phase) => {
    if (!phase) return -1;
    const index = PHASE_ORDER.findIndex(p => phase.includes(p.split('-')[1]));
    return index >= 0 ? index : 999; // Put unknown phases at the end
  };
  
  // Helper function to check if item is incomplete
  const isItemIncomplete = (status) => {
    if (!status) return true;
    const statusStr = String(status).toLowerCase().trim();
    // N/A is not incomplete - it's "not applicable"
    if (statusStr === 'n/a') return false;
    // Item is incomplete if it's not checked/completed
    return statusStr === 'no' || statusStr === '' || statusStr === 'false' || status === false || status === 0;
  };
  
  // Helper function to check if a status indicates completion
  const isCompletedStatus = (status) => {
    if (!status) return false;
    const statusStr = String(status).toLowerCase().trim();
    
    // Explicitly exclude N/A - it's not a completion status, it's a "not applicable" status
    if (statusStr === 'n/a') return false;
    
    // Only recognize 'Done' as the valid completed status for subitems
    // Remove ambiguous terms like 'completed' which can be confused with project status
    return statusStr === 'done' || status === true || status === 1;
  };
  
  // Handle delayed removal for items from previous stages only
  const handleDelayedRemoval = useCallback((subitemId, newStatus, itemPhase) => {
    // Only apply delayed removal if we're showing incomplete items section
    const currentPhase = getPhaseForStatus(modelingStatus);
    if (!currentPhase || currentPhase === '00-Startup') return; // No previous phases for Startup
    
    // Check if this item is from a PREVIOUS phase (not current phase)
    const currentPhaseIndex = getPhaseIndex(currentPhase);
    const itemPhaseIndex = getPhaseIndex(itemPhase);
    const isFromPreviousPhase = itemPhaseIndex >= 0 && itemPhaseIndex < currentPhaseIndex;
    
    // Only apply delayed removal to items from previous phases that are being completed
    if (isFromPreviousPhase && isCompletedStatus(newStatus)) {
      // Mark item as recently completed
      setCompletedItems(prev => new Map(prev).set(subitemId, Date.now()));
      
      // Clear any existing timer for this item
      if (timersRef.current.has(subitemId)) {
        clearTimeout(timersRef.current.get(subitemId));
      }
      
      // Set 3-second timer for removal
      const timer = setTimeout(() => {
        setItemsToRemove(prev => new Set(prev).add(subitemId));
        setCompletedItems(prev => {
          const newMap = new Map(prev);
          newMap.delete(subitemId);
          return newMap;
        });
        timersRef.current.delete(subitemId);
      }, 3000);
      
      timersRef.current.set(subitemId, timer);
    }
  }, [modelingStatus]);
  
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleStatusChange = useCallback(async (subitemId, newStatus, itemPhase) => {
    const updateKey = `${subitemId}-status`;
    if (updatingItems.has(updateKey)) return;

    console.log('🔄 Status update initiated:', { subitemId, newStatus, typeof: typeof newStatus });

    // Validate and sanitize the status value before sending
    let validatedStatus = newStatus;

    // Check for any string that might contain "Completed" and map to "Done"
    if (validatedStatus && typeof validatedStatus === 'string') {
      const statusLower = validatedStatus.toLowerCase().trim();

      // Debug: Log the exact value we're about to send
      console.log('📝 Status value check:', {
        original: newStatus,
        trimmed: validatedStatus.trim(),
        lowercase: statusLower,
        length: validatedStatus.length,
        charCodes: Array.from(validatedStatus).map(c => c.charCodeAt(0))
      });

      // Map any variation of "Completed" to "Done"
      if (statusLower === 'completed' || statusLower === 'complete') {
        console.warn(`⚠️ Mapping "${validatedStatus}" to "Done" in UI`);
        validatedStatus = 'Done';
      }

      // Ensure we're using one of the valid enum values
      const validEnumValues = ['Done', 'Working on it', 'Stuck', 'N/A'];
      if (validatedStatus && !validEnumValues.includes(validatedStatus)) {
        console.error(`❌ Invalid status detected: "${validatedStatus}". Setting to null.`);
        validatedStatus = null;
      }
    }

    setUpdatingItems(prev => new Set(prev).add(updateKey));
    
    try {
      // First, update just the status to ensure it works
      let updateData = {
        subitemStatus: validatedStatus || null
      };
      
      // Then try to get current user for automatic assignment
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (user && user.email) {
          console.log('👤 Current user email:', user.email);
          
          // Look up employee by email
          try {
            const employee = await SubitemsService.getEmployeeByEmail(user.email);
            
            if (employee) {
              updateData.employeeId = validatedStatus ? employee.id : null;
              updateData.user = validatedStatus ? employee.name : null; // Keep for backward compatibility
              
              console.log('✅ Employee found:', {
                employeeId: employee.id,
                employeeName: employee.name
              });
            } else {
              console.warn('⚠️ No employee found for email:', user.email);
              // Use email prefix as fallback
              const fallbackName = user.email.split('@')[0];
              updateData.user = validatedStatus ? fallbackName : null;
            }
          } catch (employeeError) {
            console.error('❌ Error looking up employee:', employeeError);
            // Continue with status update even if employee lookup fails
          }
        } else {
          console.warn('⚠️ No authenticated user found');
        }
      } catch (authError) {
        console.error('❌ Error getting current user:', authError);
        // Continue with status update even if auth fails
      }
      
      // Update the subitem
      const updatedItem = await SubitemsService.updateSubitem(subitemId, updateData);
      
      // Call parent update handler if provided
      if (onSubitemUpdate) {
        onSubitemUpdate(updatedItem);
      }
      
      // Handle delayed removal for items from previous stages only
      handleDelayedRemoval(subitemId, validatedStatus, itemPhase);
    } catch (error) {
      console.error('❌ Failed to update subitem status:', error);
      // Show user-friendly error message
      alert(`Failed to update status: ${error.message || 'Unknown error'}`);
    } finally {
      setUpdatingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(updateKey);
        return newSet;
      });
    }
  }, [updatingItems, onSubitemUpdate, handleDelayedRemoval]);

  // Cleanup timers on unmount
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      // Clear all pending timers when component unmounts
      timers.forEach(timer => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  if (loading) {
    return <LoadingSpinner message="Loading subitems..." />;
  }

  if (error) {
    return (
      <div style={{ padding: 'var(--space-4)' }}>
        <ErrorMessage
          message={`Failed to load subitems: ${error}`}
          onRetry={() => window.location.reload()}
        />
      </div>
    );
  }

  if (!localSubitems || localSubitems.length === 0) {
    return (
      <div style={{
        padding: 'var(--space-8)',
        textAlign: 'center',
        color: 'var(--gray-400)',
        fontSize: 'var(--text-sm)',
        backgroundColor: 'var(--dark-elevated)',
        borderRadius: 'var(--radius-lg)',
        margin: 'var(--space-4)'
      }}>
        <p>No subitems found for this phase</p>
      </div>
    );
  }

  // Determine current phase
  const currentPhase = getPhaseForStatus(modelingStatus);
  
  // Sort helper function
  const sortItems = (items) => {
    return items.sort((a, b) => {
      // First sort by phase index
      const phaseIndexA = getPhaseIndex(a.phase);
      const phaseIndexB = getPhaseIndex(b.phase);
      
      if (phaseIndexA !== phaseIndexB) {
        return phaseIndexA - phaseIndexB;
      }
      
      // Then sort by order within same phase
      return (a.order || 0) - (b.order || 0);
    });
  };
  
  let currentPhaseItems = [];
  let previousIncompleteItems = [];
  let allProjectSubitems = [];
  
  if (currentPhase) {
    console.log(`📊 ${modelingStatus} Modal - Building three sections...`);
    
    // SECTION 1: Current stage items (ALL items for the current phase - completed and incomplete)
    // Completed items in current stage remain visible to show progress
    // N/A items in current stage remain visible so users can toggle them back if needed
    // Only items from PREVIOUS stages get removed after 3-second delay when completed
    currentPhaseItems = localSubitems.filter(item => {
      const isCurrentPhase = item.phase === currentPhase;
      const notRemoved = !itemsToRemove.has(item.id);
      return isCurrentPhase && notRemoved;
    });
    
    // SECTION 2: Previous Stages - Not Completed (incomplete items from previous phases only)
    const currentPhaseIndex = getPhaseIndex(currentPhase);
    previousIncompleteItems = localSubitems.filter(item => {
      const itemPhaseIndex = getPhaseIndex(item.phase);
      const isFromPreviousPhase = itemPhaseIndex >= 0 && itemPhaseIndex < currentPhaseIndex;
      const isIncomplete = isItemIncomplete(item.subitemStatus || item.status);
      // Remove N/A filtering - N/A items should remain visible
      const notRemoved = !itemsToRemove.has(item.id);
      
      return isFromPreviousPhase && isIncomplete && notRemoved;
    });
    
    // SECTION 3: All Project Subitems (everything including N/A)
    // This section should ALWAYS show ALL items, never filter based on removal
    allProjectSubitems = [...localSubitems];
    
    // Sort all sections
    currentPhaseItems = sortItems(currentPhaseItems);
    previousIncompleteItems = sortItems(previousIncompleteItems);
    allProjectSubitems = sortItems(allProjectSubitems);
    
    console.log(`📊 ${modelingStatus} Modal Section Counts:`, {
      currentPhase: currentPhaseItems.length,
      previousIncomplete: previousIncompleteItems.length,
      allProject: allProjectSubitems.length
    });
  }

  const renderSubitemRow = (subitem, showPhaseLabel = false) => {
    const isStatusUpdating = updatingItems.has(`${subitem.id}-status`);
    const isRecentlyCompleted = completedItems.has(subitem.id);
    const shouldRemove = itemsToRemove.has(subitem.id);

    // Don't render items that are marked for removal
    if (shouldRemove) {
      return null;
    }

    // Check if this item is from a previous phase (incomplete item)
    const currentPhaseForRow = getPhaseForStatus(modelingStatus);
    const isIncompleteFromPrevious = currentPhaseForRow && getPhaseIndex(subitem.phase) < getPhaseIndex(currentPhaseForRow);
    const isCurrentPhaseItem = subitem.phase === currentPhaseForRow;

    const currentStatus = subitem.subitemStatus || subitem.status;

    // Build row styles
    const rowStyles = {
      borderBottom: '1px solid var(--gray-600)',
      transition: 'all 0.15s ease',
      opacity: 1,
      backgroundColor: 'transparent'
    };

    // Apply conditional styles
    if (isIncompleteFromPrevious || subitem.isIncompleteItem) {
      rowStyles.background = 'transparent';
    }

    if (isRecentlyCompleted) {
      rowStyles.background = 'transparent';
    }

    // Add background for completed items in current stage (they stay visible)
    if (isCurrentPhaseItem && isCompletedStatus(currentStatus)) {
      rowStyles.background = 'rgba(16, 185, 129, 0.12)';
      rowStyles.borderLeft = '3px solid #10b981';
    }

    // Add background for stuck items (red highlight)
    if (isCurrentPhaseItem && currentStatus === 'Stuck') {
      rowStyles.background = 'rgba(239, 68, 68, 0.12)';
      rowStyles.borderLeft = '3px solid #ef4444';
    }

    // Hover styles
    const handleMouseEnter = (e) => {
      // Don't override status highlights for completed/stuck items in current phase
      if (isCurrentPhaseItem && isCompletedStatus(currentStatus)) {
        e.currentTarget.style.background = 'rgba(16, 185, 129, 0.18)'; // Slightly brighter green on hover
      } else if (isCurrentPhaseItem && currentStatus === 'Stuck') {
        e.currentTarget.style.background = 'rgba(239, 68, 68, 0.18)'; // Slightly brighter red on hover
      } else if (isCurrentPhaseItem && currentStatus === 'Working on it') {
        e.currentTarget.style.background = 'rgba(59, 130, 246, 0.12)'; // Add highlight for working on it
      } else if (isIncompleteFromPrevious) {
        e.currentTarget.style.background = 'rgba(245, 158, 11, 0.08)';
      } else {
        e.currentTarget.style.background = 'rgba(59, 130, 246, 0.08)';
      }
    };

    const handleMouseLeave = (e) => {
      if (isCurrentPhaseItem && isCompletedStatus(currentStatus)) {
        e.currentTarget.style.background = 'rgba(16, 185, 129, 0.12)';
      } else if (isCurrentPhaseItem && currentStatus === 'Stuck') {
        e.currentTarget.style.background = 'rgba(239, 68, 68, 0.12)';
      } else if (isRecentlyCompleted) {
        e.currentTarget.style.background = 'transparent';
      } else {
        e.currentTarget.style.background = 'transparent';
      }
    };

    return (
      <tr
        key={subitem.id}
        style={rowStyles}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <td style={{
          padding: '8px 12px',
          fontSize: '14px',
          color: 'var(--gray-200)',
          minWidth: '250px',
          fontWeight: '500'
        }}>
          {/* Show colored stage badge in Section 2 and Section 3 */}
          {(showPhaseLabel || isIncompleteFromPrevious) && subitem.phase && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '10px',
                padding: '3px 8px',
                borderRadius: '12px',
                fontSize: '10px',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                whiteSpace: 'nowrap',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                verticalAlign: 'middle',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                backgroundColor: STAGE_COLORS[subitem.phase]?.bg || '#6b7280',
                color: STAGE_COLORS[subitem.phase]?.text || '#ffffff'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.05)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.1)';
              }}
            >
              {subitem.phase.replace(/^\d+-/, '').toUpperCase()}
            </span>
          )}
          <span style={{
            verticalAlign: 'middle',
            lineHeight: '1.4',
            textDecoration: currentStatus === 'N/A' ? 'line-through' : 'none',
            opacity: currentStatus === 'N/A' ? 0.6 : 1
          }}>
            {subitem.name}
          </span>
        </td>
        <td style={{
          padding: '8px 12px',
          fontSize: '14px',
          color: 'var(--gray-200)',
          minWidth: '140px',
          textAlign: 'left',
          verticalAlign: 'middle'
        }}>
          <SubitemStatusIndicator
            status={subitem.subitemStatus || ''}
            onStatusChange={(newStatus) => handleStatusChange(subitem.id, newStatus, subitem.phase)}
            isUpdating={isStatusUpdating}
          />
        </td>
        <td style={{
          padding: '8px 12px',
          fontSize: '14px',
          color: 'var(--gray-200)',
          minWidth: '150px'
        }}>
          {/* Display employee name from relationship or fallback to user text */}
          <span style={{
            display: 'inline-block',
            padding: '4px 8px',
            fontSize: '13px',
            color: 'var(--gray-100)',
            backgroundColor: 'var(--dark-bg)',
            borderRadius: '4px',
            fontWeight: '500'
          }}>
            {subitem.employee?.name || subitem.user || 'Unassigned'}
          </span>
          {isRecentlyCompleted && (
            <span
              title="Item will be removed in 3 seconds"
              style={{
                marginLeft: '8px',
                fontSize: '14px',
                animation: 'pulseRemoval 1s infinite',
                color: 'var(--warning)'
              }}
            >
              🕐
            </span>
          )}
        </td>
      </tr>
    );
  };

  // Render three-section structure for all phases
  if (currentPhase) {
    return (
      <div style={{
        width: '100%',
        overflowX: 'auto',
        overflowY: 'auto',
        flex: 1,
        minHeight: 0,
        maxHeight: '500px',
        backgroundColor: 'var(--dark-elevated)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--gray-600)',
        position: 'relative',
        zIndex: 1
      }}>
        <style>{`
          @keyframes pulseRemoval {
            0%, 100% { opacity: 0.6; }
            50% { opacity: 1; }
          }
        `}</style>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          borderSpacing: 0
        }}>
          <thead style={{
            backgroundColor: 'var(--dark-bg)',
            position: 'sticky',
            top: 0,
            zIndex: 10,
            borderBottom: '1px solid var(--gray-600)'
          }}>
            <tr>
              <th style={{
                padding: '6px 16px',
                textAlign: 'left',
                fontWeight: '600',
                fontSize: '13px',
                color: 'var(--gray-400)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                whiteSpace: 'nowrap',
                minWidth: '250px'
              }}>Name</th>
              <th style={{
                padding: '6px 16px',
                textAlign: 'left',
                fontWeight: '600',
                fontSize: '13px',
                color: 'var(--gray-400)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                whiteSpace: 'nowrap',
                minWidth: '140px'
              }}>Status</th>
              <th style={{
                padding: '6px 16px',
                textAlign: 'left',
                fontWeight: '600',
                fontSize: '13px',
                color: 'var(--gray-400)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                whiteSpace: 'nowrap',
                minWidth: '150px'
              }}>User</th>
            </tr>
          </thead>
          <tbody>
            {/* SECTION 1: Current Stage Items */}
            <tr style={{ borderTop: 'none' }}>
              <td colSpan="3" style={{
                padding: '12px 16px 4px 16px',
                background: 'transparent',
                fontWeight: '600',
                border: 'none',
                verticalAlign: 'middle'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  fontSize: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.8px',
                  fontWeight: '700',
                  color: 'var(--cta-blue)'
                }}>
                  <span>{modelingStatus} Items ({currentPhaseItems.length})</span>
                </div>
              </td>
            </tr>
            {currentPhaseItems.length > 0 ? (
              currentPhaseItems.map(item => renderSubitemRow(item, false))
            ) : (
              <tr>
                <td colSpan="3" style={{
                  padding: '16px',
                  textAlign: 'center',
                  color: 'var(--gray-500)',
                  fontSize: '13px',
                  fontStyle: 'italic',
                  backgroundColor: 'var(--dark-elevated)'
                }}>
                  No items for {modelingStatus}
                </td>
              </tr>
            )}

            {/* SECTION 2: Previous Stages - Not Completed */}
            <tr style={{ borderTop: '1px solid var(--gray-600)' }}>
              <td colSpan="3" style={{
                padding: '16px 16px 4px 16px',
                background: 'transparent',
                fontWeight: '600',
                border: 'none',
                verticalAlign: 'middle'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  fontSize: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.8px',
                  fontWeight: '700',
                  color: 'var(--warning)'
                }}>
                  <span>Previous Stages - Not Completed ({previousIncompleteItems.length})</span>
                </div>
              </td>
            </tr>
            {previousIncompleteItems.length > 0 ? (
              previousIncompleteItems.map(item => renderSubitemRow(item, true))
            ) : (
              <tr>
                <td colSpan="3" style={{
                  padding: '16px',
                  textAlign: 'center',
                  color: 'var(--gray-500)',
                  fontSize: '13px',
                  fontStyle: 'italic',
                  backgroundColor: 'var(--dark-elevated)'
                }}>
                  All previous stage items are complete
                </td>
              </tr>
            )}

            {/* SECTION 3: All Project Subitems (Collapsible) */}
            <tr ref={allItemsSectionRef} style={{ borderTop: '1px solid var(--gray-600)' }}>
              <td colSpan="3" style={{
                padding: '16px 16px 8px 16px',
                background: 'transparent',
                fontWeight: '600',
                border: 'none',
                verticalAlign: 'middle'
              }}>
                <div
                  onClick={() => {
                    setAllItemsCollapsed(!allItemsCollapsed);
                    // Scroll to show expanded content after a short delay
                    if (allItemsCollapsed && allItemsSectionRef.current) {
                      setTimeout(() => {
                        allItemsSectionRef.current?.scrollIntoView({
                          behavior: 'smooth',
                          block: 'start'
                        });
                      }, 100);
                    }
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    color: 'var(--gray-400)',
                    fontSize: '12px',
                    userSelect: 'none',
                    padding: '4px 8px',
                    margin: '-4px -8px',
                    borderRadius: '6px',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(59, 130, 246, 0.05)';
                    e.currentTarget.style.color = 'var(--cta-blue)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = 'var(--gray-400)';
                  }}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{
                      transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      transform: allItemsCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                      color: 'currentColor',
                      flexShrink: 0
                    }}
                  >
                    <path
                      d="M6 9L12 15L18 9"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span style={{ lineHeight: '16px' }}>All Project Subitems ({allProjectSubitems.length})</span>
                </div>
              </td>
            </tr>
            {!allItemsCollapsed && (
              allProjectSubitems.length > 0 ? (
                allProjectSubitems.map(item => renderSubitemRow(item, true))
              ) : (
                <tr>
                  <td colSpan="3" style={{
                    padding: '16px',
                    textAlign: 'center',
                    color: 'var(--gray-500)',
                    fontSize: '13px',
                    fontStyle: 'italic',
                    backgroundColor: 'var(--dark-elevated)'
                  }}>
                    No subitems found for this project
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>
    );
  }

  // Default rendering for other phases
  return (
    <div style={{
      width: '100%',
      overflowX: 'auto',
      backgroundColor: 'var(--dark-elevated)',
      borderRadius: 'var(--radius-lg)',
      border: '1px solid var(--gray-600)'
    }}>
      <table style={{
        width: '100%',
        borderCollapse: 'collapse',
        borderSpacing: 0
      }}>
        <thead style={{
          backgroundColor: 'var(--dark-bg)',
          borderBottom: '1px solid var(--gray-600)'
        }}>
          <tr>
            <th style={{
              padding: '6px 16px',
              textAlign: 'left',
              fontWeight: '600',
              fontSize: '13px',
              color: '#9ca3af',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              whiteSpace: 'nowrap'
            }}>Name</th>
            <th style={{
              padding: '6px 16px',
              textAlign: 'left',
              fontWeight: '600',
              fontSize: '13px',
              color: '#9ca3af',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              whiteSpace: 'nowrap'
            }}>Status</th>
            <th style={{
              padding: '6px 16px',
              textAlign: 'left',
              fontWeight: '600',
              fontSize: '13px',
              color: '#9ca3af',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              whiteSpace: 'nowrap'
            }}>User</th>
          </tr>
        </thead>
        <tbody>
          {localSubitems.map((subitem) => {
            const isStatusUpdating = updatingItems.has(`${subitem.id}-status`);

            return (
              <tr
                key={subitem.id}
                style={{
                  borderBottom: '1px solid var(--gray-600)',
                  transition: 'background-color 0.15s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(59, 130, 246, 0.08)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <td style={{
                  padding: '8px 12px',
                  fontSize: '14px',
                  color: 'var(--gray-200)',
                  fontWeight: '500'
                }}>
                  {subitem.name}
                </td>
                <td style={{
                  padding: '8px 12px',
                  fontSize: '14px',
                  color: 'var(--gray-200)'
                }}>
                  <SubitemStatusIndicator
                    status={subitem.subitemStatus || ''}
                    onStatusChange={(newStatus) => handleStatusChange(subitem.id, newStatus, subitem.phase)}
                    isUpdating={isStatusUpdating}
                  />
                </td>
                <td style={{
                  padding: '8px 12px',
                  fontSize: '14px',
                  color: 'var(--gray-200)'
                }}>
                  <span style={{
                    display: 'inline-block',
                    padding: '4px 8px',
                    fontSize: '13px',
                    color: 'var(--gray-100)',
                    backgroundColor: 'var(--dark-bg)',
                    borderRadius: '4px',
                    fontWeight: '500'
                  }}>
                    {subitem.employee?.name || subitem.user || 'Unassigned'}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
});

SubitemsTable.displayName = 'SubitemsTable';

export default SubitemsTable;